import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useLazyQuery } from '@apollo/client'
import {
  Container,
  Paper,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Alert,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
  Box,
  Snackbar,
} from '@mui/material'
import { COUNTRY_LIST, PROVINCES_LIST } from 'constants'

import { mutations, queries } from '../../graphql'
import { NotificationDialog } from '../../components/dialog/notification-dialog'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function CreateClientFund() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [isCorporate, setIsCorporate] = useState(false)
  const [hasDonorFund, setHasDonorFund] = useState(false)
  const pendingLookupEmail = useRef('')
  const [formData, setFormData] = useState({
    clientEmail: '',
    clientFirstName: '',
    clientMiddleInitials: '',
    clientLastName: '',
    clientPhone: '',
    corporateName: '',
    address: {
      lineOne: '',
      lineTwo: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'Canada',
    },
  })
  const [errors, setErrors] = useState({})
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' })

  const [lookupClient] = useLazyQuery(queries.fundClaims.lookupClientByEmail, {
    onCompleted: (data) => {
      if (pendingLookupEmail.current) {
        setHasDonorFund(data.lookupClientByEmail.hasDonorFund)
        pendingLookupEmail.current = ''
      }
    },
    fetchPolicy: 'network-only',
  })

  const [createClientFund, { loading, error }] = useMutation(mutations.fundClaims.createClientFund, {
    onCompleted: () => {
      setSuccessDialogOpen(true)
    },
  })

  const handleEmailBlur = () => {
    const email = formData.clientEmail
    if (EMAIL_REGEX.test(email)) {
      pendingLookupEmail.current = email
      lookupClient({ variables: { email } })
    } else {
      setHasDonorFund(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'clientEmail') {
      setHasDonorFund(false)
      pendingLookupEmail.current = ''
    }
    if (name.startsWith('address.')) {
      const addressField = name.replace('address.', '')
      setFormData((prev) => {
        const updates = { [addressField]: value }
        if (addressField === 'country' && value !== prev.address.country) {
          updates.province = ''
        }
        return {
          ...prev,
          address: { ...prev.address, ...updates },
        }
      })
      if (errors.address?.[addressField]) {
        setErrors((prev) => ({
          ...prev,
          address: { ...prev.address, [addressField]: null },
        }))
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: null }))
      }
    }
  }

  const validateStep1 = () => {
    const newErrors = {}

    if (!formData.clientEmail) {
      newErrors.clientEmail = 'Email is required'
    } else if (!EMAIL_REGEX.test(formData.clientEmail)) {
      newErrors.clientEmail = 'Please enter a valid email address'
    }

    if (!formData.clientFirstName) newErrors.clientFirstName = 'First name is required'
    if (!formData.clientLastName) newErrors.clientLastName = 'Last name is required'
    if (!formData.clientPhone) newErrors.clientPhone = 'Phone number is required'
    if (isCorporate && !formData.corporateName) {
      newErrors.corporateName = 'Organization name is required for corporate funds'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = () => {
    const newErrors = { address: {} }

    if (!formData.address.lineOne) newErrors.address.lineOne = 'Address is required'
    if (!formData.address.city) newErrors.address.city = 'City is required'
    if (!formData.address.province) newErrors.address.province = 'Province is required'
    if (!formData.address.postalCode) newErrors.address.postalCode = 'Postal code is required'
    if (!formData.address.country) newErrors.address.country = 'Country is required'

    if (Object.keys(newErrors.address).length === 0) delete newErrors.address
    setErrors(newErrors)
    return !newErrors.address || Object.keys(newErrors.address).length === 0
  }

  const handleNext = async () => {
    if (!validateStep1()) return

    if (hasDonorFund) {
      try {
        await createClientFund({ variables: { input: { clientEmail: formData.clientEmail } } })
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to send invitation. Please try again.', severity: 'error' })
      }
    } else {
      setStep(2)
    }
  }

  const handleBack = () => {
    setStep(1)
    setErrors({})
    setHasDonorFund(false)
    pendingLookupEmail.current = ''
  }

  const handleSubmit = async () => {
    if (!validateStep2()) return

    try {
      await createClientFund({
        variables: {
          input: {
            clientEmail: formData.clientEmail,
            clientFirstName: formData.clientFirstName,
            clientMiddleInitials: formData.clientMiddleInitials || undefined,
            clientLastName: formData.clientLastName,
            clientPhone: formData.clientPhone,
            fundType: isCorporate ? 'corporate' : 'personal',
            corporateName: isCorporate ? formData.corporateName : undefined,
            address: {
              lineOne: formData.address.lineOne,
              lineTwo: formData.address.lineTwo || undefined,
              city: formData.address.city,
              province: formData.address.province,
              postalCode: formData.address.postalCode,
              country: formData.address.country,
            },
          },
        },
      })
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to send invitation. Please try again.', severity: 'error' })
    }
  }

  const handleCancel = () => {
    navigate('/funds')
  }

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false)
    navigate('/funds')
  }

  const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }))

  const isCanada = formData.address.country === 'Canada'
  const step1NextLabel = hasDonorFund ? 'Add Fund' : 'Continue'

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Add Client Giving Fund
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        {step === 1 && (
          <>
            <TextField
              fullWidth
              label="Client Email"
              name="clientEmail"
              value={formData.clientEmail}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              error={!!errors.clientEmail}
              helperText={errors.clientEmail}
              sx={{ mb: 2, mt: 1 }}
              required
            />

            <FormControlLabel
              size="small"
              control={<Switch checked={isCorporate} />}
              onChange={(e) => setIsCorporate(e.target.checked)}
              label="Sign up as an Organization or Business"
              sx={{ mb: 2 }}
            />

            {isCorporate && (
              <>
                <TextField
                  fullWidth
                  label="Organization/Business Name"
                  name="corporateName"
                  value={formData.corporateName}
                  onChange={handleChange}
                  error={!!errors.corporateName}
                  helperText={errors.corporateName}
                  sx={{ mb: 2 }}
                  required
                />
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
                  Primary Contact Person
                </Typography>
              </>
            )}

            <TextField
              fullWidth
              label="First Name"
              name="clientFirstName"
              value={formData.clientFirstName}
              onChange={handleChange}
              error={!!errors.clientFirstName}
              helperText={errors.clientFirstName}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Middle Initials"
              name="clientMiddleInitials"
              value={formData.clientMiddleInitials}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Last Name"
              name="clientLastName"
              value={formData.clientLastName}
              onChange={handleChange}
              error={!!errors.clientLastName}
              helperText={errors.clientLastName}
              sx={{ mb: 2 }}
              required
            />

            <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
              Contact
            </Typography>

            <TextField
              fullWidth
              label="Phone Number"
              name="clientPhone"
              value={formData.clientPhone}
              onChange={handleChange}
              error={!!errors.clientPhone}
              helperText={errors.clientPhone}
              sx={{ mb: 2 }}
              required
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button variant="contained" onClick={handleNext} disabled={loading}>
                {loading ? 'Adding...' : step1NextLabel}
              </Button>
            </Box>
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
              Client Address
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Address is used for tax receipts only. No physical mail will be sent.
            </Typography>

            <TextField
              fullWidth
              label="Address Line One"
              name="address.lineOne"
              value={formData.address.lineOne}
              onChange={handleChange}
              error={!!errors.address?.lineOne}
              helperText={errors.address?.lineOne}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Address Line Two"
              name="address.lineTwo"
              value={formData.address.lineTwo}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="City"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
              error={!!errors.address?.city}
              helperText={errors.address?.city}
              sx={{ mb: 2 }}
              required
            />

            {isCanada ? (
              <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.address?.province}>
                <InputLabel id="province-label">Province *</InputLabel>
                <Select
                  labelId="province-label"
                  label="Province *"
                  name="address.province"
                  value={formData.address.province}
                  onChange={handleChange}
                >
                  {PROVINCES_LIST.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
                {errors.address?.province && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.address.province}
                  </Typography>
                )}
              </FormControl>
            ) : (
              <TextField
                fullWidth
                label="Province/State"
                name="address.province"
                value={formData.address.province}
                onChange={handleChange}
                error={!!errors.address?.province}
                helperText={errors.address?.province}
                sx={{ mb: 2 }}
                required
              />
            )}

            <TextField
              fullWidth
              label="Postal Code"
              name="address.postalCode"
              value={formData.address.postalCode}
              onChange={handleChange}
              error={!!errors.address?.postalCode}
              helperText={errors.address?.postalCode}
              sx={{ mb: 2 }}
              required
            />

            <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.address?.country}>
              <InputLabel id="country-label">Country *</InputLabel>
              <Select
                labelId="country-label"
                label="Country *"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
              >
                {COUNTRY_LIST.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
              {errors.address?.country && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.address.country}
                </Typography>
              )}
            </FormControl>

            <Alert severity="info" sx={{ mt: 2 }}>
              The client will receive an email invitation to claim their Giving Fund.
            </Alert>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button onClick={handleBack}>Back</Button>
              <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Adding...' : 'Add Fund'}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <NotificationDialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        type="success"
        title="Invitation Sent"
        message="An invite to your client has been sent."
        actions={[{ label: 'OK', onClick: handleSuccessDialogClose, variant: 'contained', autoFocus: true }]}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
